/**
 * Thanh ben: anh thu nho cac trang, chon trang, va keo tha de sap xep lai.
 *
 * =============================================================================
 *  THANH BEN HIEN SO DO TRANG, KHONG HIEN TEP GOC
 * =============================================================================
 *  Danh sach o day duoc dung tu so do trang DA PHAN GIAI (viewer/planView.ts) -
 *  tuc la thu tu trang SE CO trong tep xuat ra. Xoa mot trang thi anh thu nho
 *  cua no bien mat ngay; keo mot trang len thi no len ngay.
 *
 *  KHUNG XEM CHINH VE TU CUNG DANH SACH DO. Truoc day khong phai vay: khung xem
 *  giu tai lieu pdf.js da mo luc dau va ve theo thu tu goc, nen ghep them mot
 *  tep vao thi thanh ben hien 4 trang con khung xem van hien 3. Tep xuat ra thi
 *  dung - tuc la man hinh chinh dang hien mot tai lieu khong con ton tai. Hai
 *  khung hinh gio doc chung mot ket qua resolvePlanView(), nen chung khong the
 *  lech nhau nua.
 *
 * =============================================================================
 *  AO HOA ANH THU NHO - DUNG LAM HONG CHO NAY
 * =============================================================================
 *  Mot tai lieu 500 trang ma ve het thu nho van la nua gigabyte. Moi anh chi
 *  duoc ve khi that su lot vao vung nhin cua thanh ben (IntersectionObserver),
 *  va canvas bi thu ve 0x0 khi ra khoi vung do de tra bo nho anh lai cho trinh
 *  duyet ngay.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { PdfCore } from '@pdfeditor/types';
import { asPageSource } from '../core';
import { isCancelled } from '../core/pdfjs';
import type { PdfRenderTask } from '../core/pdfjs';
import { ORIGINAL_DOC_ID } from '../core/pages/plan';
import type { ViewSlot } from '../viewer/planView';
import type { PlanDocuments } from '../viewer/usePlanDocuments';

const THUMB_WIDTH = 132;

/** Cach nguoi dung bam vao mot anh thu nho. */
export type SelectMode = 'replace' | 'toggle' | 'range';

export interface SidebarProps {
  core: PdfCore | null;
  /**
   * So do trang DA PHAN GIAI: kich thuoc, do xoay tong, va tai lieu nguon cua
   * tung o. Cung dung mang ma khung xem chinh nhan.
   */
  slots: readonly ViewSlot[];
  /** Tai lieu pdf.js theo ma nguon - de ve duoc ca trang cua tep da ghep. */
  documents: PlanDocuments;
  /** Chi so O TRANG khung xem chinh dang o. */
  currentPage: number;
  selected: ReadonlySet<number>;
  onSelectSlot: (slotIndex: number, mode: SelectMode) => void;
  onGoToPage: (pageIndex: number) => void;
  /** Keo tha: go o `from` ra roi chen vao vi tri `to`. */
  onReorder: (from: number, to: number) => void;
  fileName: string | null;
  /** Nhan cua tai lieu ngoai da ghep, de hien tren o trang tuong ung. */
  sourceLabel: (docId: string) => string | null;
  /** Thanh thao tac theo ngu canh, do PagePanel dat vao. */
  toolbar?: ReactNode;
}

export function Sidebar({
  core,
  slots,
  documents,
  currentPage,
  selected,
  onSelectSlot,
  onGoToPage,
  onReorder,
  fileName,
  sourceLabel,
  toolbar,
}: SidebarProps) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  // Cuon anh thu nho cua trang hien hanh vao vung nhin khi nguoi dung cuon
  // trong tai lieu chinh.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    // Theo O TRANG. Truoc day cho nay tim theo chi so trang GOC, nen mot trang
    // xuat hien hai lan trong so do se luon keo thanh ben ve ban dau tien - ke
    // ca khi nguoi dung dang xem ban thu hai.
    const el = list.querySelector<HTMLElement>(`[data-thumb-slot="${currentPage}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [currentPage, slots]);

  return (
    <aside className="sidebar" aria-label="Danh sách trang">
      <div className="sidebarHeader">
        <span className="sidebarTitle">Trang</span>
        <span className="sidebarCount">{slots.length}</span>
      </div>
      {fileName && (
        <div className="sidebarFile" title={fileName}>
          {fileName}
        </div>
      )}
      {toolbar}
      <div className="thumbList" ref={listRef} onDragEnd={() => { setDragFrom(null); setDragOver(null); }}>
        {slots.map((slot) => (
          <Thumbnail
            key={`${slot.slotIndex}:${slot.docId}:${slot.sourcePageIndex ?? 'blank'}`}
            core={core}
            slot={slot}
            documents={documents}
            active={slot.slotIndex === currentPage}
            selected={selected.has(slot.slotIndex)}
            dragging={dragFrom === slot.slotIndex}
            dropTarget={
              dragOver === slot.slotIndex && dragFrom !== null && dragFrom !== slot.slotIndex
            }
            sourceLabel={sourceLabel}
            onSelect={(mode) => {
              onSelectSlot(slot.slotIndex, mode);
              // Nhay den O TRANG, khong den trang goc. Khung xem chinh gio cung
              // danh chi so theo so do, nen hai ben dung chung mot he chi so.
              onGoToPage(slot.slotIndex);
            }}
            onDragStart={() => setDragFrom(slot.slotIndex)}
            onDragOver={() => setDragOver(slot.slotIndex)}
            onDrop={() => {
              if (dragFrom !== null && dragFrom !== slot.slotIndex) {
                onReorder(dragFrom, slot.slotIndex);
              }
              setDragFrom(null);
              setDragOver(null);
            }}
          />
        ))}
      </div>
    </aside>
  );
}

function Thumbnail({
  core,
  slot,
  documents,
  active,
  selected,
  dragging,
  dropTarget,
  sourceLabel,
  onSelect,
  onDragStart,
  onDragOver,
  onDrop,
}: {
  core: PdfCore | null;
  slot: ViewSlot;
  documents: PlanDocuments;
  active: boolean;
  selected: boolean;
  dragging: boolean;
  dropTarget: boolean;
  sourceLabel: (docId: string) => string | null;
  onSelect: (mode: SelectMode) => void;
  onDragStart: () => void;
  onDragOver: () => void;
  onDrop: () => void;
}) {
  const hostRef = useRef<HTMLButtonElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [visible, setVisible] = useState(false);

  /**
   * Tai lieu chua trang nay - KHONG nhat thiet la tai lieu dang mo.
   *
   * Truoc day cho nay chi ve duoc trang cua tai lieu goc, nen anh thu nho cua
   * trang vua ghep vao chi la mot o xam co ten tep. Bay gio moi tai lieu nguon
   * co doi tuong tai lieu pdf.js cua rieng no, va o trang ghep ve ra dung noi
   * dung cua no.
   */
  const sourceDoc = documents.get(slot.docId)?.doc ?? null;
  const renderable = slot.isBlank ? null : slot.sourcePageIndex;

  // Do xoay va kich thuoc lay THANG tu o trang da phan giai: chung da cong
  // /Rotate cua trang voi phan xoay them, dung mot lan, o mot cho.
  const totalRotation = slot.rotation;

  const size = useMemo(() => {
    const swapped = totalRotation === 90 || totalRotation === 270;
    const w = swapped ? slot.heightPt : slot.widthPt;
    const h = swapped ? slot.widthPt : slot.heightPt;
    const scale = THUMB_WIDTH / Math.max(1, w);
    return { width: THUMB_WIDTH, height: Math.round(h * scale), scale };
  }, [slot.widthPt, slot.heightPt, totalRotation]);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) setVisible(entry.isIntersecting);
      },
      { root: el.closest('.thumbList'), rootMargin: '300px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!visible || !core || !sourceDoc || renderable === null) {
      // Ra khoi vung nhin: tra bo nho anh lai cho trinh duyet ngay.
      canvas.width = 0;
      canvas.height = 0;
      return;
    }

    const source = asPageSource(core);
    if (!source) return;

    let cancelled = false;
    let task: PdfRenderTask | null = null;

    void source.getPageProxy(sourceDoc, renderable).then((proxy) => {
      if (cancelled || !proxy) return;
      // Do xoay dua THANG cho pdf.js chu khong xoay canvas bang CSS: xoay bang
      // CSS thi anh dung nhung o chua van giu kich thuoc cu, va trang ngang
      // hien ra bi cat hai dau.
      const viewport = proxy.getViewport({ scale: size.scale, rotation: totalRotation });
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) return;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      task = proxy.render({ canvasContext: ctx, viewport });
      task.promise.catch((err: unknown) => {
        if (!isCancelled(err) && !cancelled) console.warn('[thumb] loi ve', err);
      });
    });

    return () => {
      cancelled = true;
      if (task) {
        try {
          task.cancel();
        } catch {
          // da xong roi - vo hai
        }
      }
    };
  }, [visible, core, sourceDoc, renderable, size.scale, totalRotation]);

  const isBlank = slot.isBlank;
  const foreignLabel = !isBlank && slot.docId !== ORIGINAL_DOC_ID ? sourceLabel(slot.docId) : null;

  const classes = ['thumb'];
  if (active) classes.push('active');
  if (selected) classes.push('selected');
  if (dragging) classes.push('dragging');
  if (dropTarget) classes.push('dropTarget');

  return (
    <button
      type="button"
      ref={hostRef}
      data-thumb-slot={slot.slotIndex}
      {...(slot.originalPageIndex !== null
        ? { 'data-thumb-page': slot.originalPageIndex }
        : {})}
      className={classes.join(' ')}
      draggable
      onDragStart={onDragStart}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
      onClick={(e) => {
        if (e.shiftKey) onSelect('range');
        else if (e.ctrlKey || e.metaKey) onSelect('toggle');
        else onSelect('replace');
      }}
      aria-current={active ? 'page' : undefined}
      aria-pressed={selected}
      aria-label={`Trang ${slot.slotIndex + 1}${selected ? ' (đang chọn)' : ''}`}
      title={
        foreignLabel
          ? `Trang ${(slot.sourcePageIndex ?? 0) + 1} của tệp “${foreignLabel}”`
          : isBlank
            ? 'Trang trắng vừa chèn'
            : `Trang ${slot.slotIndex + 1}`
      }
    >
      <span
        className="thumbFrame"
        style={{ width: `${size.width}px`, height: `${size.height}px` }}
      >
        <canvas ref={canvasRef} />
        {isBlank && <span className="thumbPlaceholder">Trang trắng</span>}
        {/*
          O giu cho CHI khi tai lieu nguon chua mo xong. Truoc day moi trang
          ghep deu chi la o giu cho, vi thanh ben khong ve duoc trang cua tai
          lieu khac. Gio no ve duoc, nen o giu cho chi con nghia "dang tai".
        */}
        {slot.pending && <span className="thumbPlaceholder">Đang tải…</span>}
        {foreignLabel && !slot.pending && (
          <span className="thumbSource" title={`Trang ghép từ tệp “${foreignLabel}”`}>
            Ghép
          </span>
        )}
        {!isBlank && !slot.pending && !slot.hasTextLayer && (
          <span className="thumbBadge" title="Trang này không có lớp văn bản — cần OCR">
            OCR
          </span>
        )}
        {slot.rotationDelta !== 0 && (
          <span className="thumbRotated" title={`Đã xoay thêm ${slot.rotationDelta}°`}>
            {slot.rotationDelta}°
          </span>
        )}
      </span>
      <span className="thumbNumber">{slot.slotIndex + 1}</span>
    </button>
  );
}
